import { Check, Users, Zap, Infinity as InfinityIcon } from 'lucide-react'
import { FadeIn } from '../components/ui/FadeIn'
import { GlassCard } from '../components/ui/GlassCard'
import { cn } from '../lib/cn'
import { useLanguage } from '../contexts/LanguageContext'
import type { LucideIcon } from 'lucide-react'

interface Plan {
  nameKey: string
  price: string | number
  descriptionKey: string
  userLimit: number | 'unlimited'
  icon: LucideIcon
  features: string[]
  popular?: boolean
  ctaKey: string
  ctaHref?: string
}

export function PricingSection() {
  const { t } = useLanguage()

  const plans: Plan[] = [
    {
      nameKey: 'pricing.standard',
      price: 20,
      descriptionKey: 'pricing.standard.desc',
      userLimit: 20,
      icon: Users,
      features: [
        `${t('pricing.feature.users')} (20)`,
        t('pricing.feature.history'),
        t('pricing.feature.incidents'),
        t('pricing.feature.locations'),
        t('pricing.feature.sos'),
        t('pricing.feature.checkpoint'),
        t('pricing.feature.walkie'),
      ],
      ctaKey: 'pricing.cta.start',
      ctaHref: '#contacto',
    },
    {
      nameKey: 'pricing.pro',
      price: 35,
      descriptionKey: 'pricing.pro.desc',
      userLimit: 40,
      icon: Zap,
      features: [
        `${t('pricing.feature.users')} (40)`,
        t('pricing.feature.history'),
        t('pricing.feature.incidents'),
        t('pricing.feature.locations'),
        t('pricing.feature.sos'),
        t('pricing.feature.checkpoint'),
        t('pricing.feature.walkie'),
      ],
      popular: true,
      ctaKey: 'pricing.cta.start',
      ctaHref: '#contacto',
    },
    {
      nameKey: 'pricing.promax',
      price: t('pricing.contact'),
      descriptionKey: 'pricing.promax.desc',
      userLimit: 'unlimited',
      icon: InfinityIcon,
      features: [
        `${t('pricing.feature.users')} (${t('pricing.unlimited')})`,
        t('pricing.feature.history'),
        t('pricing.feature.incidents'),
        t('pricing.feature.locations'),
        t('pricing.feature.sos'),
        t('pricing.feature.checkpoint'),
        t('pricing.feature.walkie'),
      ],
      ctaKey: 'pricing.cta.contact',
      ctaHref: '#contacto',
    },
  ]

  return (
    <section id="precios" className="relative">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs font-semibold tracking-[0.2em] text-nexus-blue/80">
              {t('pricing.label')}
            </div>
            <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
              {t('pricing.title')}
            </h2>
            <p className="mt-3 text-pretty text-white/70">
              {t('pricing.description')}
            </p>
          </div>
        </FadeIn>

        <div className="mt-10 grid gap-6 md:grid-cols-3 lg:gap-8">
          {plans.map((plan, index) => {
            const Icon = plan.icon
            const isPopular = plan.popular
            const isContact = typeof plan.price === 'string' && plan.price === t('pricing.contact')

            return (
              <FadeIn key={plan.nameKey} delay={0.1 * index} className="h-full">
                <GlassCard
                  className={cn(
                    'h-full flex flex-col relative',
                    isPopular && 'ring-2 ring-nexus-blue/50 shadow-glow',
                  )}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="glass-strong rounded-full px-3 py-1 text-xs font-semibold text-nexus-blue">
                        {t('pricing.popular')}
                      </span>
                    </div>
                  )}

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={cn(
                          'grid size-12 place-items-center rounded-xl border',
                          isPopular
                            ? 'bg-nexus-blue/10 border-nexus-blue/30'
                            : 'bg-white/10 border-white/10',
                        )}
                      >
                        <Icon
                          className={cn(
                            'size-6',
                            isPopular ? 'text-nexus-blue' : 'text-nexus-cyan',
                          )}
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold tracking-tight">{t(plan.nameKey)}</h3>
                        <p className="text-xs text-white/65">{t(plan.descriptionKey)}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      {isContact ? (
                        <div>
                          <div className="text-2xl font-bold text-white">{t('pricing.contact')}</div>
                          <div className="text-sm text-white/60">{t('pricing.contact.desc')}</div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-white">${plan.price}</span>
                            <span className="text-sm text-white/60">{t('pricing.month')}</span>
                          </div>
                          <div className="text-xs text-white/50 mt-1">{t('pricing.per.user')}</div>
                        </div>
                      )}
                    </div>

                    <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="size-4 text-nexus-blue" />
                        <span className="text-white/90">
                          {plan.userLimit === 'unlimited' ? (
                            <>
                              <InfinityIcon className="inline size-4 mx-1" />
                              {t('pricing.unlimited')}
                            </>
                          ) : (
                            `${plan.userLimit} ${t('pricing.users')}`
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="text-xs font-semibold text-white/60 mb-2">{t('pricing.includes')}</div>
                      {plan.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start gap-2">
                          <Check className="size-4 text-nexus-cyan mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-white/80">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <a
                      href={plan.ctaHref}
                      className={cn(
                        'block w-full rounded-xl px-4 py-3 text-center text-sm font-semibold transition-all',
                        isPopular
                          ? 'bg-nexus-blue hover:bg-nexus-blue/90 text-white shadow-glow'
                          : isContact
                            ? 'glass border border-white/10 hover:bg-white/10 text-white'
                            : 'glass border border-white/10 hover:bg-white/10 text-white',
                      )}
                    >
                      {t(plan.ctaKey)}
                    </a>
                  </div>
                </GlassCard>
              </FadeIn>
            )
          })}
        </div>

        <FadeIn delay={0.1}>
          <div className="mt-10 text-center">
            <p className="text-sm text-white/60">
              {t('pricing.footer')}
            </p>
            <p className="mt-2 text-xs text-white/50">
              {t('pricing.custom')}{' '}
              <a href="#contacto" className="text-nexus-blue hover:text-nexus-cyan transition-colors">
                {t('pricing.custom.link')}
              </a>
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  )
}

